import { SubjectArea } from '../../interfaces/EOAInterface';
import { CourseMappingCellType, CourseMappingVisibleRow } from '../../interfaces/CourseMappingInterface';
import { COURSE_MAPPING_COLUMNS } from './courseMappingColumns';
import { isEmptyCellValue } from './courseMappingCellFormatters';
import {
  applyAttributeChange,
  applyCheckboxChange,
  applySubjectAreaChange,
  CourseMappingPendingChanges,
  getCheckboxAttributeKey,
  getCreditsAttributeKey,
  getDropdownAttributeKey,
  getEffectiveAttributeValue,
  getEffectiveCheckboxValue,
} from '../../utils/courseMappingChanges';
import {
  CourseMappingDropdownOption,
  mergeDropdownOptions,
  resolveLanguageLevelSelection,
  toLanguageLevelDisplayLabel,
  toLanguageLevelOptionValue,
  hasSubjectSubcategoryOptions,
  isLanguageDropdownAvailable,
  toSubjectSubcategoryOptions,
} from './courseMappingDropdownOptions';
import CourseMappingCheckboxCell from './CourseMappingCheckboxCell';
import CourseMappingCreditsCell from './CourseMappingCreditsCell';
import CourseMappingDropdownCell from './CourseMappingDropdownCell';
import CourseMappingSelectTag from './CourseMappingSelectTag';
import CourseMappingSmartCellBody from './CourseMappingSmartCellBody';
import { getOtherDistinctValuesCount, getRowCourseId, CourseMappingRowValues } from './courseMappingTableUtils';

interface CourseMappingDropdownOptions {
  subjectAreas: CourseMappingDropdownOption[];
  subjectAreaConfig: SubjectArea[];
  languageCategories: CourseMappingDropdownOption[];
  languageLevels: CourseMappingDropdownOption[];
}

interface CourseMappingTableEditableCellProps {
  row: CourseMappingVisibleRow;
  targetCourseIds: string[];
  columnKey: string;
  cellType: CourseMappingCellType;
  value: string | boolean;
  pendingChanges: CourseMappingPendingChanges;
  dropdownOptions: CourseMappingDropdownOptions;
  showOtherValuesIndicator: boolean;
  onPendingChangesChange: (pendingChanges: CourseMappingPendingChanges) => void;
}

const renderTextCell = (value: string, otherValuesCount = 0) => (
  <CourseMappingSmartCellBody
    value={<span className="course-mapping-cell-text">{value}</span>}
    otherValuesCount={otherValuesCount}
  />
);

const renderDropdownTag = (columnKey: string, label: string) => (
  <CourseMappingSelectTag label={label} columnKey={columnKey} />
);

const renderFormattedColumnValue = (columnKey: string, value: string, otherValuesCount = 0) => {
  switch (columnKey) {
    default:
      return renderTextCell(value, otherValuesCount);
  }
};

const renderUnavailableDropdownCell = (otherValuesCount: number) => (
  <span className="course-mapping-dropdown-cell-unavailable">
    <CourseMappingSmartCellBody
      value={<span className="course-mapping-dropdown-cell-placeholder" aria-hidden="true" />}
      otherValuesCount={otherValuesCount}
    />
  </span>
);

const isDropdownUnavailable = (
  columnKey: string,
  subjectArea: string | null,
  dropdownOptions: CourseMappingDropdownOptions
): boolean => {
  if (columnKey === 'subjectSubcategory') {
    return !hasSubjectSubcategoryOptions(dropdownOptions.subjectAreaConfig, subjectArea);
  }

  if (columnKey === 'languageCategory' || columnKey === 'languageLevel') {
    return !isLanguageDropdownAvailable(subjectArea);
  }

  return false;
};

const getDropdownCellOptions = (
  columnKey: string,
  selectedValue: string | null,
  subjectArea: string | null,
  dropdownOptions: CourseMappingDropdownOptions
): CourseMappingDropdownOption[] => {
  switch (columnKey) {
    case 'subjectArea':
      return mergeDropdownOptions(dropdownOptions.subjectAreas, selectedValue);
    case 'subjectSubcategory':
      return mergeDropdownOptions(
        toSubjectSubcategoryOptions(dropdownOptions.subjectAreaConfig, subjectArea),
        selectedValue
      );
    case 'languageCategory':
      return mergeDropdownOptions(dropdownOptions.languageCategories, selectedValue);
    case 'languageLevel':
      return dropdownOptions.languageLevels;
    default:
      return [];
  }
};

const CourseMappingTableEditableCell = ({
  row,
  targetCourseIds,
  columnKey,
  cellType,
  value,
  pendingChanges,
  dropdownOptions,
  showOtherValuesIndicator,
  onPendingChangesChange,
}: CourseMappingTableEditableCellProps) => {
  const group = row.group;
  const courseId = getRowCourseId(row);
  const column = COURSE_MAPPING_COLUMNS.find((entry) => entry.key === columnKey);
  const columnLabel = column?.label ?? columnKey;
  const otherValuesCount = showOtherValuesIndicator
    ? getOtherDistinctValuesCount(group, columnKey as keyof CourseMappingRowValues, value, pendingChanges)
    : 0;

  const handleCheckboxToggle = () => {
    const attributeKey = getCheckboxAttributeKey(columnKey);
    if (!attributeKey) {
      return;
    }

    const currentValue = getEffectiveCheckboxValue(group, attributeKey, pendingChanges, courseId);
    onPendingChangesChange(applyCheckboxChange(pendingChanges, group, targetCourseIds, attributeKey, !currentValue));
  };

  const handleDropdownSelect = (selectedOptionValue: string) => {
    const attributeKey = getDropdownAttributeKey(columnKey);
    if (!attributeKey) {
      return;
    }

    if (columnKey === 'subjectArea') {
      onPendingChangesChange(applySubjectAreaChange(pendingChanges, group, targetCourseIds, selectedOptionValue));
      return;
    }

    if (columnKey === 'languageLevel') {
      const currentLevel = getEffectiveAttributeValue(group, 'language_level', pendingChanges, courseId) as
        | number
        | null;
      const nextLevel = resolveLanguageLevelSelection(selectedOptionValue, currentLevel);
      onPendingChangesChange(applyAttributeChange(pendingChanges, group, targetCourseIds, attributeKey, nextLevel));
      return;
    }

    onPendingChangesChange(
      applyAttributeChange(pendingChanges, group, targetCourseIds, attributeKey, selectedOptionValue)
    );
  };

  const handleCreditsCommit = (nextValue: number | null) => {
    const attributeKey = getCreditsAttributeKey(columnKey);
    if (!attributeKey) {
      return;
    }

    onPendingChangesChange(applyAttributeChange(pendingChanges, group, targetCourseIds, attributeKey, nextValue));
  };

  if (cellType === 'editable') {
    const attributeKey = getCreditsAttributeKey(columnKey);
    if (!attributeKey) {
      return null;
    }

    const creditsValue = getEffectiveAttributeValue(group, attributeKey, pendingChanges, courseId) as number | null;

    return (
      <CourseMappingCreditsCell
        value={creditsValue}
        ariaLabel={`${columnLabel} for ${group.name}`}
        onCommit={handleCreditsCommit}
      />
    );
  }

  if (cellType === 'dropdown') {
    const attributeKey = getDropdownAttributeKey(columnKey);
    if (!attributeKey) {
      return null;
    }

    const rawValue = getEffectiveAttributeValue(group, attributeKey, pendingChanges, courseId);

    const selectedValue =
      columnKey === 'languageLevel'
        ? toLanguageLevelOptionValue(rawValue as number | null)
        : (rawValue as string | null);

    const displayLabel =
      columnKey === 'languageLevel'
        ? toLanguageLevelDisplayLabel(rawValue as number | null)
        : (rawValue as string | null);

    const displayValue =
      displayLabel && !isEmptyCellValue(displayLabel) ? renderDropdownTag(columnKey, displayLabel) : null;

    const effectiveSubjectArea = getEffectiveAttributeValue(group, 'subject_area', pendingChanges, courseId) as
      | string
      | null;

    if (isDropdownUnavailable(columnKey, effectiveSubjectArea, dropdownOptions)) {
      return renderUnavailableDropdownCell(otherValuesCount);
    }

    return (
      <CourseMappingDropdownCell
        columnKey={columnKey}
        displayValue={displayValue}
        otherValuesCount={otherValuesCount}
        options={getDropdownCellOptions(columnKey, selectedValue, effectiveSubjectArea, dropdownOptions)}
        selectedValue={selectedValue}
        ariaLabel={`${columnLabel} for ${group.name}`}
        onSelect={handleDropdownSelect}
      />
    );
  }

  if (cellType === 'checkbox') {
    const attributeKey = getCheckboxAttributeKey(columnKey);
    if (!attributeKey) {
      return null;
    }

    const checked = getEffectiveCheckboxValue(group, attributeKey, pendingChanges, courseId);

    return (
      <CourseMappingCheckboxCell
        checked={checked}
        label={`${columnLabel} for ${group.name}`}
        onToggle={handleCheckboxToggle}
      />
    );
  }

  return renderFormattedColumnValue(columnKey, value as string, otherValuesCount);
};

export default CourseMappingTableEditableCell;
